import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Breadcrumbs,
  Button,
  Grid,
  Link,
  Typography,
  Tooltip,
} from "@mui/material";
import { useViewer } from "~/providers/viewer";
import { AddButton } from "~/components/add-button";
import { Add as AddIcon, Home as HomeIcon } from "@mui/icons-material";
import { IoOnTheMoon } from "~/components/galaxy/io-on-the-moon";
import { Config } from "~/constants/configuration_provider";
import { CanCreateOrganization } from "~/lib/iam/actions/mondoo/captain/Captain/CreateOrganization";
import { setDocumentTitle } from "~/lib/commonUtils";
import { useRegions } from "~/providers/RegionsProvider";
import { parseAssetRiskRatings } from "~/lib/arrow";
import type { Org } from "~/lib/types";

const GRID_SIZE = 3;

function OrganizationAssetCount({ org }: { org: Org }) {
  const entries =
    org.metrics?.__typename === "MetricsResponse" ? org.metrics.entries : [];
  const { total } = parseAssetRiskRatings(entries || []);

  return (
    <Grid size={3}>
      <Typography variant="body1" fontWeight="bold" lineHeight="1">
        Assets
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {total || "-"}
      </Typography>
    </Grid>
  );
}

export function RegionDashboardPage() {
  const { viewer } = useViewer();
  const { selectedRegion } = useRegions();

  const isPrivateInstance = Config.VITE_PRIVATE_INSTANCE === "true";

  const hasOrgsOrSpaces = Boolean(viewer?.organizations?.length);

  const content = (
    <Grid container spacing={3}>
      {viewer?.organizations?.map((org) => {
        const primaryOwner = org.memberStats?.owners.at(0);
        const remainingOwnersCount =
          (org.memberStats?.owners.length || 0) - (primaryOwner ? 1 : 0);

        return (
          <Grid
            key={org.id}
            id="orgs-list"
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <Box
              className="dashboard-card orgs-list-item"
              sx={{
                display: "block",
                position: "relative",
                "&::after": {
                  content: "' '",
                  position: "absolute",
                  width: "100%",
                  height: "100%",
                  top: 0,
                  left: 0,
                  borderRadius: 0.5,
                  zIndex: -1,
                  opacity: "0",
                  transition: "opacity 300ms ease-in-out",
                  boxShadow: `0px 0px 0px 2px #CCCCCC`,
                },
                "&:hover::after, &:focus::after": {
                  opacity: "80%",
                  transition: "opacity 300ms ease-in-out",
                },
              }}
              component={RouterLink}
              to={`/organization?organizationId=${org.id}`}
            >
              <Box
                className="dashboard-card-header"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  backgroundColor: "background.light",
                  p: 2,
                  borderTopLeftRadius: 4,
                  borderTopRightRadius: 4,
                }}
              >
                <Box>
                  <Typography variant="body1" fontWeight="bold" lineHeight="1">
                    {org.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {org.id}
                  </Typography>
                </Box>
              </Box>
              <Box
                className="dashboard-card-body"
                sx={{
                  backgroundColor: "background.paper",
                  display: "flex",
                  justifyContent: "space-between",
                  p: 2,
                  borderBottomLeftRadius: 4,
                  borderBottomRightRadius: 4,
                }}
              >
                <Grid container size={12}>
                  <Grid size={GRID_SIZE}>
                    <Typography
                      variant="body1"
                      fontWeight="bold"
                      lineHeight="1"
                    >
                      Spaces
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {org.spacesCount}
                    </Typography>
                  </Grid>
                  <OrganizationAssetCount org={org} />
                  {org.memberStats && (
                    <Grid size={GRID_SIZE}>
                      <Typography
                        variant="body1"
                        fontWeight="bold"
                        lineHeight="1"
                      >
                        Members
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {org.memberStats.total}
                      </Typography>
                    </Grid>
                  )}
                  {org.memberStats && (
                    <Grid size={GRID_SIZE}>
                      <Typography
                        variant="body1"
                        fontWeight="bold"
                        lineHeight="1"
                        sx={{ mb: 0.5 }}
                      >
                        Owners
                      </Typography>
                      <Tooltip
                        title={
                          org.memberStats.owners.length > 0
                            ? org.memberStats.owners.map((owner) => (
                                <Box key={owner.mrn} component="div">
                                  {owner.name}
                                </Box>
                              ))
                            : ""
                        }
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            flexDirection: "column",
                          }}
                        >
                          {primaryOwner ? (
                            <Box
                              component="span"
                              sx={{
                                maxWidth: "100%",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                display: "inline-block",
                              }}
                            >
                              {primaryOwner.name}
                            </Box>
                          ) : (
                            "—"
                          )}
                          {remainingOwnersCount > 0 &&
                            `+ ${remainingOwnersCount} more`}
                        </Typography>
                      </Tooltip>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </Box>
          </Grid>
        );
      })}

      {/* Add Org */}
      <CanCreateOrganization>
        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <Box
            className="dashboard-add-card"
            component={RouterLink}
            to="/organizations/new"
            sx={{
              borderRadius: 1,
              border: "2px dashed",
              borderColor: "background.lighter",
              color: "#7B7B7B",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
              height: "100%",
              minHeight: "144px",
              transition: (theme) =>
                theme.transitions.create(["color", "border-color"]),

              "&:hover": {
                borderColor: "background.lightest",
                color: "text.primary",
              },
            }}
          >
            <AddIcon fontSize="large" />
            <Typography variant="body2" textTransform="uppercase">
              Add Organization
            </Typography>
          </Box>
        </Grid>
      </CanCreateOrganization>
    </Grid>
  );

  const emptyContent = (
    <Grid container justifyContent="center" spacing={2}>
      <Grid size={12}>
        <Box id="welcome" sx={{ textAlign: "center", m: 2, p: 2 }}>
          <Box
            sx={{
              display: "flex",
              alighItems: "center",
              justifyContent: "center",
              py: 2,
              width: 1,
            }}
          >
            <Box sx={{ width: { xs: 100, sm: 150, md: 200 } }}>
              <IoOnTheMoon />
            </Box>
          </Box>
          {!isPrivateInstance && <Typography variant="h2">Welcome</Typography>}
          {!isPrivateInstance && (
            <Typography variant="body1">
              You don't have any organizations or spaces yet. Start by creating
              one.
            </Typography>
          )}
          {isPrivateInstance && (
            <Typography
              variant="h5"
              sx={{ textTransform: "uppercase", fontWeight: 700, mb: 3 }}
            >
              Welcome to your Mondoo dedicated account
            </Typography>
          )}
          {isPrivateInstance && (
            <Typography variant="body1" sx={{ color: "text.secondary" }}>
              Please wait for an administrator to grant you access to your
              company's organizations.
            </Typography>
          )}
        </Box>
      </Grid>
      <Grid size={12}>
        <Box sx={{ textAlign: "center" }}>
          <CanCreateOrganization>
            <Button
              variant="contained"
              color="primary"
              component={RouterLink}
              to="/organizations/new"
            >
              New Organization
            </Button>
          </CanCreateOrganization>
        </Box>
      </Grid>
    </Grid>
  );

  const breadcrumbs = [
    <Link
      key="/dashboard"
      component={RouterLink}
      to={`/dashboard`}
      display="flex"
    >
      <HomeIcon fontSize="inherit" />
    </Link>,
    <Typography key="/organizations">Organizations</Typography>,
  ];

  setDocumentTitle("Dashboard");

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 3, overflowWrap: "anywhere" }} separator="›">
        {breadcrumbs}
      </Breadcrumbs>
      {/* Heading */}
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Grid size={12}>
          <Box>
            <Typography
              variant="h4"
              component="h1"
              fontWeight="bold"
              textTransform="uppercase"
            >
              {!isPrivateInstance
                ? `${selectedRegion.name} Region`
                : "Organizations"}
            </Typography>
          </Box>
        </Grid>
        <CanCreateOrganization>
          <Grid size={12}>
            <AddButton id="add-organization" href="/organizations/new" />
          </Grid>
        </CanCreateOrganization>
      </Box>
      <Box my={3}>{hasOrgsOrSpaces ? content : emptyContent}</Box>
    </Box>
  );
}
